import type { NastavitvePartnerjev, StranPrometa } from '@koda/shared';
import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';

import type { ParametriPartnerjev } from '../lib/api';
import { useBesedila } from '../lib/jezik';

/** Koliko partnerjev posebej; ostali so v vrstici »Ostali«. */
const STEVILA_PARTNERJEV = [5, 10, 20, 50];
const STEVILA_KUPCEV = [10, 20, 50];

interface Lastnosti {
  podjetje: string;
  vrsta: 'partnerji' | 'rvc-kupci';
  /** Nastavitve podjetja; `null`, dokler se berejo ali jih ni bilo mogoče prebrati. */
  nastavitve: NastavitvePartnerjev | null;
  /** Izbira iz naslova strani. */
  izbira: ParametriPartnerjev;
  /** Nova izbira; nastavi se v naslov strani (prazna polja se odstranijo). */
  naIzbiro: (izbira: ParametriPartnerjev) => void;
}

/** Konto je predpona izvirne šifre: 1 do 6 števk (enako preverja strežnik). */
const KONTO = /^[0-9]{1,6}$/;

/** Razčlenjen vnos kontov: veljavni konti in deli, ki niso konto. */
export interface RazclenjeniKonti {
  /** Veljavne predpone brez dvojnikov, urejene. */
  konti: string[];
  /** Deli vnosa, ki niso konto (npr. »23x«), v vrstnem redu vnosa. */
  napacni: string[];
}

/**
 * Predpone iz vnosa »22, 23 7600«. Napačen del se ne izpusti tiho: vrne se
 * posebej, da ga vmesnik pokaže in prikaza oziroma shranjevanja ne izvede –
 * sicer bi poročilo zajelo manj kontov, kot uporabnik misli.
 */
export function razcleniKonte(vnos: string): RazclenjeniKonti {
  const deli = vnos
    .split(/[\s,;]+/)
    .map((k) => k.trim())
    .filter((k) => k !== '');
  return {
    konti: [...new Set(deli.filter((k) => KONTO.test(k)))].sort(),
    napacni: [...new Set(deli.filter((k) => !KONTO.test(k)))],
  };
}

/**
 * Izbira nad zavihkoma Partnerji in RVC po kupcih (docs/ARHITEKTURA.md, 6.5).
 *
 * Partnerji: gumbi s prednastavitvami podjetja (»Privzete« pokaže privzeto
 * odprte, vsaka druga eno tabelo), »Po meri« odpre vnos kontov in strani.
 * Kateri gumb je pritisnjen, pove naslov strani, ne lokalno stanje – zato
 * izbira preživi osvežitev in se stran da deliti. Oba zavihka imata izbiro,
 * koliko partnerjev je prikazanih posebej, in povezavo do nastavitev, ki
 * jih poleg servisa ureja tudi skrbnik stranke.
 */
export function FilterPartnerjev({ podjetje, vrsta, nastavitve, izbira, naIzbiro }: Lastnosti): JSX.Element {
  const b = useBesedila();
  const prednastavitve = nastavitve?.prednastavitve ?? [];
  const predpone = izbira.predpone ?? [];
  const ujema = (p: { predpone: string[]; stran: StranPrometa }): boolean =>
    izbira.stran === p.stran && predpone.length === p.predpone.length && p.predpone.every((k, i) => predpone[i] === k);
  const izbranaPrednastavitev = prednastavitve.find(ujema);
  const jeIzbira = predpone.length > 0;
  // Po meri je odprto, kadar je v naslovu izbira brez prednastavitve ali ko
  // uporabnik klikne gumb; zapre se z izbiro prednastavitve.
  const [odprtoPoMeri, setOdprtoPoMeri] = useState(false);
  const poMeri = odprtoPoMeri || (jeIzbira && izbranaPrednastavitev === undefined);
  const [vnosKontov, setVnosKontov] = useState(predpone.join(', '));
  const [vnosStrani, setVnosStrani] = useState<StranPrometa>(izbira.stran ?? 'dobro');
  const [napakaVnosa, setNapakaVnosa] = useState<string | null>(null);

  const stevila = vrsta === 'partnerji' ? STEVILA_PARTNERJEV : STEVILA_KUPCEV;
  const privzetoStevilo = vrsta === 'partnerji' ? 5 : 10;
  const n = izbira.n ?? privzetoStevilo;

  const izberiStevilo = (novo: number): void => {
    naIzbiro({ ...izbira, n: novo === privzetoStevilo ? undefined : novo });
  };

  return (
    <div className="filter-partnerjev">
      {vrsta === 'partnerji' && (
        <div className="prednastavitve-partnerjev" role="group" aria-label={b.pa.izbiraKontov}>
          <button
            type="button"
            aria-pressed={!jeIzbira && !poMeri}
            onClick={() => {
              setOdprtoPoMeri(false);
              naIzbiro({ n: izbira.n });
            }}
          >
            {b.pa.privzete}
          </button>
          {prednastavitve.map((p) => (
            <button
              key={p.koda}
              type="button"
              aria-pressed={!poMeri && izbranaPrednastavitev?.koda === p.koda}
              title={`${b.pa.konti}: ${p.predpone.join(', ')} · ${b.pa.strani[p.stran]}`}
              onClick={() => {
                setOdprtoPoMeri(false);
                setVnosKontov(p.predpone.join(', '));
                setVnosStrani(p.stran);
                naIzbiro({ predpone: p.predpone, stran: p.stran, n: izbira.n });
              }}
            >
              {p.naziv}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={poMeri}
            onClick={() => {
              setOdprtoPoMeri(true);
            }}
          >
            {b.pa.poMeri}
          </button>
        </div>
      )}

      {vrsta === 'partnerji' && poMeri && (
        <form
          className="po-meri-partnerjev"
          onSubmit={(d) => {
            d.preventDefault();
            const { konti, napacni } = razcleniKonte(vnosKontov);
            if (napacni.length > 0) {
              setNapakaVnosa(b.pa.napacniKonti(napacni.join(', ')));
              return;
            }
            if (konti.length === 0) {
              setNapakaVnosa(b.pa.vnesiKonto);
              return;
            }
            setNapakaVnosa(null);
            naIzbiro({ predpone: konti, stran: vnosStrani, n: izbira.n });
          }}
        >
          <label>
            <span>{b.pa.konti}</span>
            <input
              type="text"
              value={vnosKontov}
              placeholder="22, 23"
              aria-describedby="namig-kontov"
              aria-invalid={napakaVnosa !== null}
              onChange={(d) => {
                setVnosKontov(d.target.value);
                setNapakaVnosa(null);
              }}
            />
          </label>
          <label>
            <span>{b.pa.stran}</span>
            <select
              value={vnosStrani}
              onChange={(d) => {
                setVnosStrani(d.target.value === 'breme' ? 'breme' : 'dobro');
              }}
            >
              <option value="dobro">{b.pa.strani.dobro}</option>
              <option value="breme">{b.pa.strani.breme}</option>
            </select>
          </label>
          <button type="submit" className="gumb">
            {b.pa.pokazi}
          </button>
          <span id="namig-kontov" className="drobno">
            {b.pa.namigKontov}
          </span>
          {napakaVnosa !== null && (
            <span className="napaka" role="alert">
              {napakaVnosa}
            </span>
          )}
        </form>
      )}

      {vrsta === 'rvc-kupci' && (
        /* Najboljši ali najslabši RVC najprej: pri najslabših so na vrhu kupci
           z najnižjo, tudi negativno razliko v ceni. */
        <div className="prednastavitve-partnerjev" role="group" aria-label={b.pa.razvrstitev}>
          <button
            type="button"
            aria-pressed={izbira.vrstni !== 'najslabsi'}
            onClick={() => {
              naIzbiro({ ...izbira, vrstni: undefined });
            }}
          >
            {b.pa.najboljsiRvc}
          </button>
          <button
            type="button"
            aria-pressed={izbira.vrstni === 'najslabsi'}
            onClick={() => {
              naIzbiro({ ...izbira, vrstni: 'najslabsi' });
            }}
          >
            {b.pa.najslabsiRvc}
          </button>
        </div>
      )}

      <div className="stevilo-partnerjev">
        <label className="drobno">
          {b.pa.prikazi}
          <select
            value={String(n)}
            onChange={(d) => {
              izberiStevilo(Number.parseInt(d.target.value, 10));
            }}
          >
            {(stevila.includes(n) ? stevila : [n, ...stevila].sort((a, c) => a - c)).map((s) => (
              <option key={s} value={String(s)}>
                {b.pa.prvih(s)}
              </option>
            ))}
          </select>
        </label>
        <Link className="drobno" to={`/podjetje/${podjetje}/nastavitve/partnerji`}>
          {b.pa.nastavitve}
        </Link>
      </div>
    </div>
  );
}
